const CAMERA_EVENT_TYPES = {
  UNSUPPORTED: "CAMERA_UNSUPPORTED",
  INSECURE_CONTEXT: "CAMERA_INSECURE_CONTEXT",
  PERMISSION_DENIED: "CAMERA_PERMISSION_DENIED",
  DEVICE_NOT_FOUND: "CAMERA_DEVICE_NOT_FOUND",
  DEVICE_BUSY: "CAMERA_DEVICE_BUSY",
  NO_FRAME: "CAMERA_NO_FRAME",
  BLACK_FRAME: "CAMERA_BLACK_FRAME",
  STREAM_ENDED: "CAMERA_STREAM_ENDED",
  UPLOAD_FAILED: "CAMERA_UPLOAD_FAILED",
  VIDEO_CONNECTION_ERROR: "VIDEO_CONNECTION_ERROR",
  RECOVERED: "CAMERA_RECOVERED",
  UNKNOWN: "CAMERA_UNKNOWN_ERROR",
};

const PUBLIC_CAMERA_MESSAGES = {
  [CAMERA_EVENT_TYPES.UNSUPPORTED]:
    "当前浏览器不支持摄像头采集，请更换浏览器或设备后重试。",
  [CAMERA_EVENT_TYPES.INSECURE_CONTEXT]:
    "当前页面环境不支持摄像头调用，请使用系统提供的考试入口重新进入。",
  [CAMERA_EVENT_TYPES.PERMISSION_DENIED]:
    "摄像头权限未开启，请在浏览器或系统设置中允许使用摄像头。",
  [CAMERA_EVENT_TYPES.DEVICE_NOT_FOUND]:
    "未检测到摄像头，请确认摄像头已连接并可正常使用。",
  [CAMERA_EVENT_TYPES.DEVICE_BUSY]:
    "摄像头可能被其他应用占用，请关闭相关应用后重试。",
  [CAMERA_EVENT_TYPES.NO_FRAME]:
    "暂时未获取到摄像头画面，系统会自动尝试恢复。",
  [CAMERA_EVENT_TYPES.BLACK_FRAME]:
    "摄像头画面异常偏暗，请检查遮挡、光线或摄像头状态。",
  [CAMERA_EVENT_TYPES.STREAM_ENDED]:
    "摄像头连接已中断，系统会自动尝试恢复。",
  [CAMERA_EVENT_TYPES.UPLOAD_FAILED]:
    "监控画面上传暂时失败，系统会继续自动重试，不影响继续答题。",
  [CAMERA_EVENT_TYPES.VIDEO_CONNECTION_ERROR]:
    "视频监控连接暂时异常，系统会自动恢复，不影响继续答题。",
  [CAMERA_EVENT_TYPES.RECOVERED]: "摄像头画面已恢复正常。",
  [CAMERA_EVENT_TYPES.UNKNOWN]:
    "摄像头暂时不可用，请检查设备和权限后重试。",
};

const FORBIDDEN_TECHNICAL_TERMS = [/peer/i, /peerjs/i, /peerid/i, /信令/i];
const RECOVERY_DELAYS = [1000, 3000, 8000, 15000];

function containsForbiddenTechnicalTerms(message) {
  const text = String(message || "");
  return FORBIDDEN_TECHNICAL_TERMS.some((pattern) => pattern.test(text));
}

function getPublicCameraMessage(eventType) {
  return (
    PUBLIC_CAMERA_MESSAGES[eventType] ||
    PUBLIC_CAMERA_MESSAGES[CAMERA_EVENT_TYPES.UNKNOWN]
  );
}

function classifyGetUserMediaError(error, env) {
  const runtime = env || {};
  if (runtime.isSecureContext === false) {
    return buildCameraResult(CAMERA_EVENT_TYPES.INSECURE_CONTEXT);
  }

  const name = error && error.name ? String(error.name) : "";
  const message = error && error.message ? String(error.message) : "";
  const normalizedMessage = message.toLowerCase();

  if (
    name === "NotAllowedError" ||
    name === "PermissionDeniedError" ||
    name === "SecurityError"
  ) {
    return buildCameraResult(CAMERA_EVENT_TYPES.PERMISSION_DENIED);
  }

  if (name === "NotFoundError" || name === "DevicesNotFoundError") {
    return buildCameraResult(CAMERA_EVENT_TYPES.DEVICE_NOT_FOUND);
  }

  if (
    name === "NotReadableError" ||
    name === "TrackStartError" ||
    name === "AbortError"
  ) {
    return buildCameraResult(CAMERA_EVENT_TYPES.DEVICE_BUSY);
  }

  if (
    name === "NotSupportedError" ||
    normalizedMessage.includes("getusermedia is not implemented") ||
    normalizedMessage.includes("getusermedia is not supported") ||
    normalizedMessage.includes("not implemented in this browser")
  ) {
    return buildCameraResult(CAMERA_EVENT_TYPES.UNSUPPORTED);
  }

  return buildCameraResult(CAMERA_EVENT_TYPES.UNKNOWN);
}

function buildCameraResult(eventType) {
  return {
    eventType,
    message: getPublicCameraMessage(eventType),
  };
}

function frameStatsFromCanvas(canvas) {
  if (!canvas || typeof canvas.getContext !== "function") {
    return { mean: 0, stddev: 0, pixelCount: 0 };
  }

  const width = canvas.width || 0;
  const height = canvas.height || 0;
  if (width <= 0 || height <= 0) {
    return { mean: 0, stddev: 0, pixelCount: 0 };
  }

  let context;
  try {
    context = canvas.getContext("2d");
  } catch (error) {
    return { mean: 0, stddev: 0, pixelCount: 0 };
  }

  if (!context || typeof context.getImageData !== "function") {
    return { mean: 0, stddev: 0, pixelCount: 0 };
  }

  let imageData;
  try {
    imageData = context.getImageData(0, 0, width, height);
  } catch (error) {
    return { mean: 0, stddev: 0, pixelCount: 0 };
  }

  const data = imageData && imageData.data ? imageData.data : [];
  const pixelCount = Math.floor(data.length / 4);
  if (pixelCount <= 0) {
    return { mean: 0, stddev: 0, pixelCount: 0 };
  }

  let sum = 0;
  let squareSum = 0;
  const sampleLength = pixelCount * 4;
  for (let index = 0; index < sampleLength; index += 4) {
    const brightness =
      data[index] * 0.299 + data[index + 1] * 0.587 + data[index + 2] * 0.114;
    sum += brightness;
    squareSum += brightness * brightness;
  }

  const mean = sum / pixelCount;
  const variance = Math.max(squareSum / pixelCount - mean * mean, 0);
  return {
    mean,
    stddev: Math.sqrt(variance),
    pixelCount,
  };
}

function isLikelyBlankFrameStats(stats) {
  if (!stats || typeof stats !== "object") {
    return true;
  }

  const mean = Number(stats.mean);
  const stddev = Number(stats.stddev);
  if (!Number.isFinite(mean) || !Number.isFinite(stddev)) {
    return true;
  }

  return mean <= 5 && stddev <= 5;
}

function nextRecoveryDelay(attempt) {
  const attemptNumber = Number(attempt);
  const normalizedAttempt = Number.isFinite(attemptNumber)
    ? Math.floor(attemptNumber)
    : 0;
  const index = Math.max(
    0,
    Math.min(normalizedAttempt, RECOVERY_DELAYS.length - 1)
  );
  return RECOVERY_DELAYS[index];
}

module.exports = {
  CAMERA_EVENT_TYPES,
  classifyGetUserMediaError,
  containsForbiddenTechnicalTerms,
  frameStatsFromCanvas,
  getPublicCameraMessage,
  isLikelyBlankFrameStats,
  nextRecoveryDelay,
};
